<script setup>
import {computed, ref} from 'vue';
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue';
import {useI18n} from 'vue-i18n';
import {formatAmount, formatDate, formatNumber} from '@/helper';
import {bondDetailViewer} from '@/utils/features';
import BondDetailContent from '@/components/bonds/BondDetailContent.vue';

/**
 * Výběr dluhopisů do kalkulačky ASO/SO (tlačítko „Přidat dluhopis“).
 * Lze vybrat více dluhopisů najednou; už přidané a nedostupné se nenabízejí. U každého je odkaz Detail.
 */
const props = defineProps({
  // nabídka z kroku Kalkulačka (bonds.available)
  bonds: {type: Array, required: true},
  // id dluhopisů, které už jsou v kalkulačce
  selectedIds: {type: Array, default: () => []},
});
// confirm(ids) – vybrané dluhopisy, cancel – zavření bez výběru
const emit = defineEmits(['confirm', 'cancel']);

const {t} = useI18n();
const isOpen = ref(true);
const chosen = ref([]);

/** Nabízí se jen dostupný dluhopis, který ještě není přidaný a má volné kusy. */
const offered = computed(() => props.bonds.filter(bond => bond.is_available
    && !props.selectedIds.includes(bond.id)
    && (bond.limits?.can_buy === null || bond.limits?.can_buy === undefined || bond.limits.can_buy >= (bond.limits.min_quantity ?? 1))));

// detail dluhopisu: klient z klientského API, poradce i administrace z API poradce (viz bondDetailViewer)
const {canShow: canShowDetail, advisor: isAdvisor} = bondDetailViewer();
// dluhopis, jehož detail je v okně zobrazený místo výběru
const detailBond = ref(null);

function toggle(id) {
  chosen.value = chosen.value.includes(id) ? chosen.value.filter(value => value !== id) : [...chosen.value, id];
}

function close(action) {
  isOpen.value = false;
  if (action === 'confirm') {
    emit('confirm', chosen.value);
  } else {
    emit('cancel');
  }
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="close('cancel')" class="modal show d-block" style="z-index: 1060;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" :class="detailBond ? 'modal-xl' : 'modal-lg'" style="z-index: 1060">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <!-- detail dluhopisu ve stejném okně (vnořený dialog se zavíral hned po otevření), výběr zůstává zachovaný -->
            <template v-if="detailBond">
              <div class="modal-header">
                <DialogTitle as="h3" class="modal-title">{{ detailBond.name }}</DialogTitle>
                <i class="btn-close cursor-pointer" @click="close('cancel')" :aria-label="t('onboardingBonds.close')"></i>
              </div>
              <div class="modal-body">
                <BondDetailContent :key="detailBond.id" :bond-id="detailBond.id" :advisor="isAdvisor" embedded/>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-outline-tenant" @click="detailBond = null"><i class="mdi mdi-chevron-left"></i> {{ t('onboardingBonds.back_to_select') }}</button>
              </div>
            </template>
            <template v-else>
              <div class="modal-header">
                <DialogTitle as="h3" class="modal-title">{{ t('onboardingBonds.select_title') }}</DialogTitle>
                <i class="btn-close cursor-pointer" @click="close('cancel')" :aria-label="t('onboardingBonds.close')"></i>
              </div>
              <div class="modal-body">
                <div v-if="!offered.length" class="text-muted">{{ t('onboardingBonds.select_empty') }}</div>
                <!-- řádek = label s checkboxem a vedle něj tlačítko Detail (mimo label, aby klik nepřepínal výběr) -->
                <div v-for="bond of offered" :key="bond.id" class="d-flex gap-3 align-items-start border rounded p-3 mb-2" :class="{'border-primary': chosen.includes(bond.id)}">
                  <label class="d-flex gap-3 align-items-start flex-fill cursor-pointer mb-0">
                    <input type="checkbox" class="form-check-input mt-1" :checked="chosen.includes(bond.id)" @change="toggle(bond.id)">
                    <span class="flex-fill">
                      <span class="fw-bold d-block">{{ bond.name }}</span>
                      <small class="text-muted d-block">{{ bond.isin }}<span v-if="bond.issuer"> · {{ bond.issuer }}</span></small>
                      <small class="d-block">
                        {{ bond.coupon_rate === null ? '---' : formatNumber(bond.coupon_rate) + ' % p.a.' }}
                        · {{ t('onboardingBonds.maturity') }} {{ bond.maturity_date ? formatDate(bond.maturity_date, 'd.m.Y') : '---' }}
                        · {{ t('onboardingBonds.price_per_piece') }} {{ formatAmount(bond.nominal_value, bond.currency, 0) }}
                      </small>
                    </span>
                  </label>
                  <button v-if="canShowDetail" type="button" class="btn btn-link p-0 flex-shrink-0 text-decoration-none" @click="detailBond = bond">
                    <i class="mdi mdi-eye"></i> {{ t('onboardingBonds.detail') }}
                  </button>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-outline-tenant" @click="close('cancel')">{{ t('onboardingBonds.close') }}</button>
                <button type="button" class="btn btn-primary" :disabled="!chosen.length" @click="close('confirm')">{{ t('onboardingBonds.select_confirm') }}</button>
              </div>
            </template>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
